import {
  type ClientLearningEvent,
  clientLearningEventSchema,
  clientLearningEventTypeSchema,
  isAllowedClientEventPayloadKey,
} from "@history-sim/api-contracts";
import type { LearningEvent } from "@history-sim/domain";

export type EventSyncDisposition =
  | { readonly kind: "enqueue"; readonly event: ClientLearningEvent }
  | { readonly kind: "server-authoritative" }
  | { readonly kind: "invalid-client-event" };

export function mapDomainEventForSync(event: LearningEvent): EventSyncDisposition {
  const type = clientLearningEventTypeSchema.safeParse(event.type);
  if (!type.success) return { kind: "server-authoritative" };

  const base = {
    eventId: event.eventId,
    occurredAt: event.occurredAt,
    type: type.data,
    ...(event.sceneId ? { sceneId: event.sceneId } : {}),
    ...(event.taskId ? { taskId: event.taskId } : {}),
    ...(event.durationMs !== undefined ? { durationMs: event.durationMs } : {}),
  };
  if (!clientLearningEventSchema.safeParse({ ...base, payload: {} }).success) {
    return { kind: "invalid-client-event" };
  }

  const sourcePayload = { ...event.payload };
  if (
    type.data === "content_viewed" &&
    sourcePayload.contentId === undefined &&
    typeof sourcePayload.sequenceId === "string"
  ) {
    sourcePayload.contentId = sourcePayload.sequenceId;
  }
  if (
    type.data === "interaction_completed" &&
    sourcePayload.interaction === undefined &&
    typeof sourcePayload.runtimeEvent === "string"
  ) {
    sourcePayload.interaction = sourcePayload.runtimeEvent;
  }

  let payload: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(sourcePayload)) {
    if (!isAllowedClientEventPayloadKey(type.data, key)) continue;
    const candidate = { ...payload, [key]: value };
    if (clientLearningEventSchema.safeParse({ ...base, payload: candidate }).success) {
      payload = candidate;
    }
  }

  const parsed = clientLearningEventSchema.safeParse({ ...base, payload });
  return parsed.success
    ? { kind: "enqueue", event: parsed.data }
    : { kind: "invalid-client-event" };
}
